/** Component Selector Activity helpers — wired on hardware selector V2 only (GS-7806). */
import { getAmplitudeEventContext } from 'amplitude/helpers/eventContext'
import { trackAmplitudeEventByName } from 'amplitude/amplitudeClient'
import type { ComponentTypes, ComponentTypesV2 } from 'types/selectComponent'
import { mapComponentTypesV2ToV1 } from 'util/misc'

export type ComponentSelectorTypes = Array<ComponentTypes | ComponentTypesV2 | string>

export type ComponentSelectorParams = {
  componentTypes: ComponentSelectorTypes
  componentCode?: string
  context?: string
}

/** Only one hardware selector is open at a time. */
let selectorSessionId = 0
let loadedSelectorSessionId: number | null = null

const normalizeComponentType = (componentType: ComponentTypes | ComponentTypesV2 | string) => {
  if (['module', 'battery', 'inverter', 'other'].includes(componentType)) return componentType
  try {
    return mapComponentTypesV2ToV1(componentType as ComponentTypesV2)
  } catch {
    return componentType
  }
}

const resolveSelectorTypes = (componentTypes: ComponentSelectorTypes) => {
  if (!componentTypes.length) return { component_type: undefined, component_types: undefined }

  const normalized = [...new Set(componentTypes.map(normalizeComponentType).filter(Boolean) as string[])]

  return {
    component_type: normalized.length === 1 ? normalized[0] : 'other',
    component_types: componentTypes.length > 1 ? componentTypes.map(String).join(',') : undefined,
  }
}

const buildSelectorEventFields = (params: ComponentSelectorParams) => {
  const { component_type, component_types } = resolveSelectorTypes(params.componentTypes)

  return {
    ...(component_type != null ? { component_type } : {}),
    ...(component_types != null ? { component_types } : {}),
    ...(params.componentCode != null ? { component_code: params.componentCode } : {}),
    ...(params.context != null ? { context: params.context } : {}),
  }
}

const trackComponentSelectorActivity = (
  action: 'opened' | 'loaded' | 'add_component_clicked',
  params: ComponentSelectorParams
) => {
  trackAmplitudeEventByName('Component Selector Activity', {
    ...getAmplitudeEventContext('design'),
    action,
    ...buildSelectorEventFields(params),
  })
}

/** Log selector open; returns a session id to pass to {@link logComponentSelectorLoaded}. */
export const logComponentSelectorOpened = (componentTypes: ComponentSelectorTypes): number => {
  selectorSessionId += 1
  loadedSelectorSessionId = null
  trackComponentSelectorActivity('opened', { componentTypes })
  return selectorSessionId
}

/** Log selector list ready. Ignores duplicate or stale callbacks for the same session. */
export const logComponentSelectorLoaded = (componentTypes: ComponentSelectorTypes, sessionId: number): void => {
  if (!sessionId || sessionId !== selectorSessionId || loadedSelectorSessionId === sessionId) return
  loadedSelectorSessionId = sessionId
  trackComponentSelectorActivity('loaded', { componentTypes })
}

export const logComponentSelectorAddClicked = (params: ComponentSelectorParams): void => {
  trackComponentSelectorActivity('add_component_clicked', params)
}

export const getAddComponentFromSelectorContext = (params: ComponentSelectorParams) => ({
  ...getAmplitudeEventContext('design'),
  ...buildSelectorEventFields(params),
})
